import React, { useState } from 'react';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Link } from 'react-router-dom';

interface PromotionPath {
  target: string;
  label: string;
}

interface PromotionChainEntry {
  from: string;
  to: string;
  from_display?: string;
  to_display?: string;
}

interface EnvironmentDef {
  name: string;
  display_name?: string;
  color?: string;
}

interface PromotionResultData {
  status: string;
  message?: string;
  approval_request_id?: string;
  evaluation_summary?: {
    pass_rate: number;
    passed_count: number;
    total_test_cases: number;
  };
}

interface ApplicablePolicyData {
  name: string;
  quality_gate_config?: {
    min_pass_rate?: number;
  };
  requires_approval?: boolean;
}

interface PromptData {
  id: string;
  name: string;
  environment?: string;
  version_number?: number;
}

interface PromotionModalProps {
  isOpen: boolean;
  onClose: () => void;
  prompt: PromptData | null;
  onPromote?: (promptId: string, target: string, releaseNotes: string | null) => void;
  isLoading?: boolean;
  promotionResult?: PromotionResultData | null;
  applicablePolicy?: ApplicablePolicyData | null;
  promotionChain?: PromotionChainEntry[] | null;
  environmentDefs?: EnvironmentDef[] | null;
}

/**
 * Default environment promotion paths (fallback when no custom chain provided)
 */
const DEFAULT_PROMOTION_PATHS: Record<string, PromotionPath> = {
  development: { target: 'staging', label: 'Staging' },
  staging: { target: 'production', label: 'Production' },
};

/**
 * Convert a hex color to Tailwind-compatible badge classes
 */
const getEnvColorFromHex = (hex: string | undefined): string => {
  if (!hex) return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
  // Map known colors to Tailwind classes
  const colorMap = {
    '#3B82F6': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    '#F59E0B': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    '#10B981': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    '#EF4444': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    '#8B5CF6': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    '#EC4899': 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
    '#06B6D4': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
    '#F97316': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  };
  return (colorMap as Record<string, string>)[hex] || 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
};

const ENV_COLORS: Record<string, string> = {
  development: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  staging: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  production: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

const getEnvColor = (env: string, envDefs?: EnvironmentDef[] | null): string => {
  // First check if we have custom environment definitions with colors
  if (envDefs && Array.isArray(envDefs)) {
    const envDef = envDefs.find(e => e.name === env);
    if (envDef?.color) return getEnvColorFromHex(envDef.color);
  }
  return ENV_COLORS[env] || 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
};

/**
 * PromotionModal - Modal for promoting a prompt to the next environment
 *
 * Supports promotion paths:
 * - development -> staging
 * - staging -> production
 *
 * Handles three possible outcomes:
 * - success: Prompt was promoted immediately
 * - pending_approval: Promotion requires approval, request created
 * - blocked: Quality gate failed, promotion blocked
 */
export const PromotionModal: React.FC<PromotionModalProps> = ({
  isOpen,
  onClose,
  prompt,
  onPromote,
  isLoading = false,
  promotionResult = null,
  applicablePolicy = null,
  promotionChain = null,
  environmentDefs = null,
}) => {
  const [releaseNotes, setReleaseNotes] = useState('');

  if (!prompt) return null;

  const currentEnv = prompt.environment || 'development';

  // Build promotion path from dynamic chain or fall back to defaults
  let promotionPath = null;
  if (promotionChain && Array.isArray(promotionChain)) {
    const chainEntry = promotionChain.find(c => c.from === currentEnv);
    if (chainEntry) {
      promotionPath = { target: chainEntry.to, label: chainEntry.to_display || chainEntry.to };
    }
  } else {
    promotionPath = DEFAULT_PROMOTION_PATHS[currentEnv] || null;
  }
  const canPromote = !!promotionPath;

  const handlePromote = () => {
    if (promotionPath && onPromote) {
      onPromote(prompt.id, promotionPath.target, releaseNotes || null);
    }
  };

  const handleClose = () => {
    setReleaseNotes('');
    onClose();
  };

  // Render success state
  if (promotionResult?.status === 'success') {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Promotion Successful"
        maxWidth="max-w-lg"
      >
        <div className="space-y-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900">
              <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              Prompt Promoted Successfully
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {promotionResult.message}
            </p>
          </div>

          <div className="flex justify-center pt-4">
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // Render pending approval state
  if (promotionResult?.status === 'pending_approval') {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Approval Required"
        maxWidth="max-w-lg"
      >
        <div className="space-y-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900">
              <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              Waiting for Approval
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {promotionResult.message}
            </p>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-yellow-700 dark:text-yellow-300">
                <p className="font-medium">Promotion requires approval</p>
                <p className="mt-1 text-yellow-600 dark:text-yellow-400">
                  Your request has been submitted and is pending review. Once approved, the prompt will be automatically promoted.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-3 pt-4">
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Link to="/ai-operations/governance?tab=approvals">
              <Button variant="primary">
                View Approvals
              </Button>
            </Link>
          </div>
        </div>
      </Modal>
    );
  }

  // Render blocked state (quality gate failed)
  if (promotionResult?.status === 'blocked') {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Promotion Blocked"
        maxWidth="max-w-lg"
      >
        <div className="space-y-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
              <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              Promotion Blocked
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {promotionResult.message}
            </p>
          </div>

          {promotionResult.evaluation_summary && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <h4 className="font-medium text-red-800 dark:text-red-300 mb-2">Quality Gate Results</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-red-600 dark:text-red-400">Pass Rate:</div>
                <div className="text-red-800 dark:text-red-300">
                  {(promotionResult.evaluation_summary.pass_rate * 100).toFixed(1)}%
                </div>
                <div className="text-red-600 dark:text-red-400">Passed:</div>
                <div className="text-red-800 dark:text-red-300">
                  {promotionResult.evaluation_summary.passed_count} / {promotionResult.evaluation_summary.total_test_cases}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-center pt-4">
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // Render default promotion form
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Promote Prompt"
      maxWidth="max-w-lg"
    >
      <div className="space-y-6">
        {/* Prompt Info */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2">
            {prompt.name}
          </h4>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400">Current:</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getEnvColor(currentEnv, environmentDefs)}`}>
              {currentEnv}
            </span>
            <span className="text-gray-400 dark:text-gray-500">v{prompt.version_number || 1}</span>
          </div>
        </div>

        {promotionPath ? (
          <>
            {/* Promotion Arrow */}
            <div className="flex items-center justify-center gap-4">
              <div className="flex flex-col items-center">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEnvColor(currentEnv, environmentDefs)}`}>
                  {currentEnv}
                </span>
                <span className="text-xs text-gray-500 mt-1">v{prompt.version_number || 1}</span>
              </div>

              <div className="flex items-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>

              <div className="flex flex-col items-center">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEnvColor(promotionPath.target, environmentDefs)}`}>
                  {promotionPath.target}
                </span>
                <span className="text-xs text-gray-500 mt-1">new version</span>
              </div>
            </div>

            {/* Release Notes */}
            <div>
              <label htmlFor="release-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Release Notes (optional)
              </label>
              <textarea id="release-notes"
                value={releaseNotes}
                onChange={(e) => setReleaseNotes(e.target.value)}
                placeholder="Describe what changed in this version..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>

            {/* Policy Info or Warning */}
            {applicablePolicy ? (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    <p className="font-medium">Policy: {applicablePolicy.name}</p>
                    <ul className="mt-1 text-blue-600 dark:text-blue-400 space-y-0.5">
                      <li className="flex items-center gap-1">
                        <span className="text-blue-400">•</span>{' '}
                        Evaluation will run automatically
                      </li>
                      <li className="flex items-center gap-1">
                        <span className="text-blue-400">•</span>
                        Min pass rate: {((applicablePolicy.quality_gate_config?.min_pass_rate || 0) * 100).toFixed(0)}%
                      </li>
                      {applicablePolicy.requires_approval && (
                        <li className="flex items-center gap-1">
                          <span className="text-blue-400">•</span>
                          <span className="font-medium">Approval required before promotion</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="text-sm text-yellow-700 dark:text-yellow-300">
                    <p className="font-medium">No policy applies</p>
                    <p className="mt-1 text-yellow-600 dark:text-yellow-400">
                      The prompt will be promoted directly without automated evaluation.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Already at production */
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-center">
            <svg className="w-12 h-12 text-green-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-600 dark:text-gray-400">
              This prompt is already at the highest environment (<span className="font-medium text-green-600 dark:text-green-400">{currentEnv}</span>).
              <br />
              No further promotion is available.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          {canPromote && (
            <Button
              variant="primary"
              onClick={handlePromote}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Promoting...
                </span>
              ) : (
                `Promote to ${promotionPath?.label}`
              )}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PromotionModal;
